import * as React from 'react';
import { useMemo, useState } from 'react';

import { useTheme } from '../theme';
import SimpleLoader from '../SimpleLoader';
import { SegmentTabs, SegmentTabType } from '@shared/ui/SegmentTabs';
import MediaQuery from '../MediaQuery/MediaQuery';
import { scrollTo } from '../utils';
import cn from 'classnames';
import { FooterMobile } from '@shared/ui';

interface ServiceWithSegmentsChildren {
	activeSegmentId: string;
}

interface Props {
	title: string;
	segments: SegmentTabType[];
	children: (args: ServiceWithSegmentsChildren) => React.ReactElement;
	onMobileBack: () => void;
	onMobileProceed: () => void;
	activeSegmentId?: string;
	onSegmentChange?: (segmentId: string) => void;
	enabledSegments?: Set<string>;
	renderAdditionalInfo?: () => React.ReactNode;
	initialSegmentId?: string;
	isLoading?: boolean;
	onClose?: () => void;
	className?: string;
}

const ServiceWithSegments: React.FC<Props> = ({
	children,
	isLoading,
	title,
	segments,
	enabledSegments,
	onMobileProceed,
	onMobileBack,
	activeSegmentId: controlledActiveSegmentId,
	onSegmentChange,
	initialSegmentId = '0',
	onClose,
	className,
	renderAdditionalInfo
}) => {
	const { ServiceWithSegments: css } = useTheme('ServiceWithSegments');
	const [internalActiveSegmentId, setInternalActiveSegmentId] = useState(initialSegmentId);

	const activeSegmentId = controlledActiveSegmentId ?? internalActiveSegmentId;
	const activeSegment = segments.find(({ id }) => id === activeSegmentId);

	const nextSegmentId = useMemo(
		() =>
			[...enabledSegments]
				.filter(segmentId => segments.find(segment => parseInt(segment.id, 10) === parseInt(segmentId, 10)))
				.sort()
				.find(segmentId => parseInt(segmentId, 10) > parseInt(activeSegmentId, 10)),
		[enabledSegments, activeSegmentId]
	);

	const setActiveSegmentId = (segmentId: string) => {
		if (!controlledActiveSegmentId) {
			setInternalActiveSegmentId(segmentId);
		}

		onSegmentChange?.(segmentId);
		scrollTo(0);
	};

	const onContinue = () => {
		if (typeof nextSegmentId !== 'undefined') {
			setActiveSegmentId(nextSegmentId);
		} else {
			onMobileProceed();
			scrollTo(0);
		}
	};

	const onBack = () => {
		const prevSegmentId = [...enabledSegments]
			.sort()
			.reverse()
			.find(segmentId => parseInt(segmentId) < parseInt(activeSegmentId));

		if (typeof prevSegmentId !== 'undefined') {
			setActiveSegmentId(prevSegmentId);
		} else {
			onMobileBack();
			scrollTo(0);
		}
	};

	return (
		<div className={cn(css.service, className)}>
			{isLoading && <SimpleLoader />}

			<MediaQuery minWidth="mobile">
				<SegmentTabs
					classes={{
						inner: css.segmentTabs__inner
					}}
					title={title}
					segments={segments}
					sticky={true}
					onSegmentClick={setActiveSegmentId}
					activeSegment={activeSegmentId}
					onClose={onClose}
				/>
			</MediaQuery>

			{renderAdditionalInfo?.()}

			<div className={css.wrapper}>
				<MediaQuery maxWidth="mobile">
					<div className={css.title}>{activeSegment.title}</div>
				</MediaQuery>

				{children({
					activeSegmentId
				})}
			</div>

			<MediaQuery maxWidth="mobile">
				<FooterMobile onBack={onBack} onContinue={onContinue} />
			</MediaQuery>
		</div>
	);
};

export default ServiceWithSegments;
